import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { ChevronRight, Search, PackageOpen, Truck, Home, ShieldCheck, MapPin, Star, Lock, Percent, Clock, Timer } from "lucide-react";
import FoundersScoreboard from "@/components/FoundersScoreboard";
import FounderSpotlight from "@/components/FounderSpotlight";
import FoundingWall from "@/components/FoundingWall";
import HalloweenHook from "@/components/HalloweenHook";
import TestedTastedLoved from "@/components/TestedTastedLoved";
import { MeatCompareSlider } from "@/components/MeatCompareSlider";
import { ProductCard } from "@/pages/Marketplace";
import { RancherCard } from "@/pages/Ranchers";

const HERO = "/assets/hero-dark-steaks.jpg";
const STORY = "https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=1400";
const USA_SEAL = "https://customer-assets-lxgj4vgw.emergentagent.net/job_farm-fresh-audit/artifacts/m4s2ynsh_Product-of-USA-Label.png";

const CATEGORY_IMAGES = {
  beef: "/assets/beef-category.jpg",
  chicken: "/assets/chicken-category.jpg",
  pork: "https://images.unsplash.com/photo-1602470520998-f4a52199a3d6?w=800",
  poultry: "/assets/chicken-category.jpg",
  lamb: "https://images.unsplash.com/photo-1608877907149-a206d75ba011?w=800",
  seafood: "https://images.unsplash.com/photo-1565680018434-b513d5e5fd47?w=800",
};

const STEPS = [
  { icon: Search, t: "Discover trusted ranchers", b: "Browse one marketplace of vetted, independent American ranchers." },
  { icon: PackageOpen, t: "Choose cuts, boxes, or drops", b: "Individual cuts, curated boxes, family bundles, and curated drops, all in one checkout." },
  { icon: Truck, t: "The rancher ships direct", b: "Each rancher packs and ships your order cold-chain, straight from their ranch." },
  { icon: Home, t: "Premium meat at your door", b: "Track every shipment, then leave a verified review for the rancher." },
];

const BENEFITS = [
  { icon: Timer, t: "Members-only curated drops", b: "Small-batch releases open to members the moment they go live." },
  { icon: Lock, t: "Members-only products", b: "Rancher-exclusive releases you won't find anywhere else." },
  { icon: Percent, t: "Preferred pricing", b: "Skip the non-member markup on every product, every rancher." },
  { icon: Truck, t: "Free shipping", b: "Members ship free from every ranch in the marketplace." },
];

const SHOP_CATEGORIES = [
  { id: "beef", label: "Beef", blurb: "Grass-fed steaks, roasts & ground", img: "/assets/category-tiles/beef.jpg" },
  { id: "chicken", label: "Chicken", blurb: "Pasture-raised whole birds & cuts", img: "/assets/category-tiles/chicken.jpg" },
  { id: "skincare", label: "Skincare", blurb: "Beef tallow balms & creams", img: "/assets/category-tiles/skincare.jpg" },
  { id: "wellness", label: "Health & Wellness", blurb: "Protein, shakes & beef sticks", img: "/assets/category-tiles/wellness.jpg" },
];

function SectionHead({ overline, title, cta, to, testId }) {
  return (
    <div className="flex items-end justify-between gap-6 flex-wrap" data-testid={testId}>
      <div><p className="overline">{overline}</p><h2 className="font-display text-3xl sm:text-4xl mt-2 leading-tight">{title}</h2></div>
      {cta && <Link to={to} className="text-sm font-medium text-[var(--ff-primary)] inline-flex items-center gap-1 hover:underline">{cta} <ChevronRight size={16}/></Link>}
    </div>
  );
}

export default function Landing() {
  const [feat, setFeat] = useState(null);
  useEffect(() => { api.get("/marketplace/featured").then(r => setFeat(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="landing-page">
      <Seo title="America's Ranchers. Delivered to Your Door." description="Shop premium 100% American-raised beef and chicken directly from trusted ranchers and farmers across the country — all in one members-only marketplace." path="/" image="https://customer-assets-lxgj4vgw.emergentagent.net/job_farm-fresh-audit/artifacts/pa0cp03i_tftu.png"/>

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden" data-testid="hero-section">
        <img src={HERO} alt="Premium marbled ribeye steaks on dark slate with rosemary and sea salt" fetchPriority="high" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/55 to-black/30" aria-hidden/>
        <div className="relative max-w-7xl mx-auto px-6 pb-20 pt-40 text-white fade-up w-full">
          <p className="overline inline-block pb-2 mb-1 border-b border-[var(--ff-accent-kraft)]/70" data-testid="hero-overline" style={{ color: "#F0E6D2", letterSpacing: "0.22em", textShadow: "0 1px 6px rgba(0,0,0,0.85)" }}>The nationwide rancher marketplace</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl mt-4 max-w-4xl leading-[1.02] font-semibold" data-testid="hero-headline" style={{ fontFamily: 'Georgia, "Times New Roman", serif', color: "#FBF7EF", textShadow: "0 2px 18px rgba(0,0,0,0.75), 0 1px 3px rgba(0,0,0,0.9)" }}>America&apos;s Ranchers. Delivered to Your Door.</h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-white leading-relaxed" data-testid="hero-subhead" style={{ textShadow: "0 1px 10px rgba(0,0,0,0.75), 0 1px 2px rgba(0,0,0,0.9)" }}>Shop premium meat directly from trusted ranchers and farmers across the country — all in one marketplace. Every product is sold and shipped by the ranch that raised it.</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/marketplace" className="btn-citrus" data-testid="hero-shop-btn">Shop the Marketplace</Link>
            <Link to="/drops" className="border border-white/70 text-white hover:bg-white hover:text-[var(--ff-primary)] rounded-full px-5 py-3 font-medium inline-flex items-center gap-1 transition-colors" data-testid="hero-drops-btn">Explore Upcoming Drops</Link>
            <Link to="/ranchers" className="border border-white/70 text-white hover:bg-white hover:text-[var(--ff-primary)] rounded-full px-5 py-3 font-medium inline-flex items-center gap-1 transition-colors" data-testid="hero-ranchers-btn">Meet Our Ranchers</Link>
            <Link to="/sell" className="text-white/85 underline underline-offset-4 px-2 py-3 text-sm inline-flex items-center" data-testid="hero-sell-btn">Become a Selling Partner →</Link>
          </div>
          <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-sm text-white/90" data-testid="hero-trust-badges">
            {["100% American ranchers", "Sold & shipped by the ranch", "Cold-chain to your door", "Members ship free"].map(b => (
              <li key={b} className="inline-flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[var(--ff-accent-kraft)] text-[var(--ff-primary)]">✓</span>{b}</li>
            ))}
          </ul>
          <FoundersScoreboard/>
        </div>
      </section>

      <HalloweenHook/>

      {/* QUALITY COMPARISON SLIDER */}
      <section className="max-w-7xl mx-auto px-6 pb-4" data-testid="compare-section">
        <div className="max-w-3xl">
          <p className="overline text-[var(--ff-primary)]">See the difference</p>
          <h2 className="font-display text-3xl sm:text-4xl mt-2 leading-tight">Drag to compare. Grocery store vs. The Farm To You.</h2>
          <p className="mt-3 text-[var(--ff-text-secondary)]">One comes wrapped in plastic with no real answer for where it came from, what's in it, or whether it was raised on antibiotics. The other is raised on an American family ranch and shipped straight to your door. Slide to see for yourself.</p>
        </div>
        <div className="mt-8">
          <MeatCompareSlider />
        </div>
      </section>

      {/* SHOP BY CATEGORY */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="shop-by-category-section">
        <SectionHead overline="Shop by category" title="One marketplace. More than meat." cta="Browse the marketplace" to="/marketplace" testId="shop-category-head"/>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {SHOP_CATEGORIES.map(c => (
            <Link
              key={c.id}
              to={`/marketplace?category=${c.id}`}
              data-testid={`category-tile-${c.id}`}
              className="group relative aspect-[4/5] rounded-3xl overflow-hidden ring-1 ring-black/5 shadow-sm hover:shadow-xl transition-shadow"
            >
              <img
                src={c.img}
                alt={c.label}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"/>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-display text-2xl text-white leading-tight">{c.label}</h3>
                <p className="text-xs text-white/80 mt-1">{c.blurb}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-white/90 group-hover:gap-2 transition-all">Shop now <ChevronRight size={14}/></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED RANCHERS */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="featured-ranchers-section">
        <SectionHead overline="Featured ranchers" title="Know exactly who raised your dinner." cta={`Meet all ${feat?.rancher_count || ""} ranchers`} to="/ranchers"/>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{(feat?.ranchers || []).slice(0, 3).map(r => <RancherCard key={r.id} r={r}/>)}</div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="how-it-works-section">
        <SectionHead overline="How the marketplace works" title="Ranch to doorstep in four steps." cta="Learn more" to="/how-it-works"/>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => { const I = s.icon; return (
            <div key={s.t} className="card-flat p-6" data-testid={`hiw-step-${i + 1}`}>
              <span className="font-display text-4xl text-[var(--ff-accent-kraft)]">0{i + 1}</span>
              <div className="mt-4 w-10 h-10 rounded-full bg-[var(--ff-primary)] text-white flex items-center justify-center"><I size={16}/></div>
              <h3 className="font-display text-xl mt-4">{s.t}</h3>
              <p className="text-sm text-[var(--ff-text-secondary)] mt-2 leading-relaxed">{s.b}</p>
            </div>); })}
        </div>
        <p className="mt-6 text-xs text-[var(--ff-text-muted)]" data-testid="hiw-disclaimer">The Farm To You operates the marketplace and facilitates transactions. Ranchers are independently operated and responsible for preparing, packaging, and shipping their own products.</p>
      </section>

      {/* STORY / TRUST */}
      <section className="relative overflow-hidden" data-testid="trust-section">
        <img src={STORY} alt="Cattle grazing on open pasture" loading="lazy" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-black/60"/>
        <div className="relative max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 text-white items-center">
          <div>
            <p className="overline text-[var(--ff-accent-kraft)]">Sourcing &amp; quality standards</p>
            <h2 className="font-display text-3xl sm:text-4xl mt-3 leading-tight">Every rancher earns the badge.</h2>
            <p className="mt-4 text-white/85 leading-relaxed">Before a ranch can sell here, we review their practices, processing partners, food-safety documentation, and shipping capability. Products are 100% American-raised — no imports, no undisclosed origin, ever.</p>
            <Link to="/legal/seller-standards" className="mt-6 inline-flex items-center gap-1 text-sm underline underline-offset-4" data-testid="trust-standards-link">Read our Seller Standards <ChevronRight size={14}/></Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[["USDA-inspected", "processing required"], ["Farm-traceable", "every product, every ranch"], ["Cold-chain", "shipped frozen, direct"], ["Verified reviews", "from delivered orders only"]].map(([a, b]) => (
              <div key={a} className="bg-white/10 border border-white/20 rounded-2xl p-5 backdrop-blur"><ShieldCheck size={18} className="text-[var(--ff-accent-kraft)]"/><p className="font-display text-xl mt-2">{a}</p><p className="text-xs text-white/75">{b}</p></div>))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-14 flex flex-col items-center text-center" data-testid="product-of-usa-section">
        <img src={USA_SEAL} alt="Product of USA" className="h-28 w-auto" loading="lazy"/>
        <p className="mt-4 font-display text-xl text-[var(--ff-primary)]">Every cut, every fillet — raised right here in America.</p>
      </section>

      {/* MEMBERSHIP */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="membership-section">
        <div className="card-flat p-8 md:p-12 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <p className="overline">Membership</p>
            <h2 className="font-display text-3xl sm:text-4xl mt-2 leading-tight">One membership. Every rancher.</h2>
            <p className="mt-4 text-[var(--ff-text-secondary)]">Starter, Preferred, and Premier plans unlock benefits across the entire marketplace — no matter which ranch you buy from.</p>
            <Link to="/pricing" className="btn-primary mt-6 inline-flex" data-testid="membership-cta">Compare plans <ChevronRight size={16}/></Link>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
            {BENEFITS.map(b => { const I = b.icon; return (
              <div key={b.t} className="flex gap-3"><div className="w-9 h-9 rounded-full bg-[var(--ff-accent-kraft)]/50 text-[var(--ff-primary)] flex items-center justify-center shrink-0"><I size={16}/></div><div><p className="font-medium">{b.t}</p><p className="text-sm text-[var(--ff-text-secondary)]">{b.b}</p></div></div>); })}
          </div>
        </div>
      </section>

      <FoundingWall/>
      <FounderSpotlight/>
      <TestedTastedLoved/>
    </div>
  );
}
